import { continents, getCountryDataList } from 'countries-list';

export type CountryInfo = {
  /** Country ISO alpha-2 code. */
  iso2: string;
  /** Country ISO alpha-3 code. */
  iso3: string;
  /** Country name in English. */
  name: string;
  /** The continent's name in English, e.g. "Europe". */
  continent: string;
  /** The country's international calling code, without the "+", e.g. 31. */
  phone: number;
};

/** List of ISO-3166 countries. */
export const countries = getCountryDataList()
  .filter((d) => !d.userAssigned)
  .map((data): CountryInfo => {
    return {
      iso2: data.iso2,
      iso3: data.iso3,
      name: data.name,
      continent: continents[data.continent],
      // Most countries have one code; for the few with several (the Dominican Republic has 1809, 1829 and 1849),
      // this is the first.
      phone: data.phone[0]!,
    };
  });

/**
 * Get country information by ISO alpha-2 or alpha-3 code.
 * @param code - The ISO alpha-2 or alpha-3 country code.
 * @returns The country information, or undefined if not found.
 */
export function getCountry(code: string): CountryInfo | undefined {
  const upperCode = code.toUpperCase();
  return countries.find((country) => country.iso2 === upperCode || country.iso3 === upperCode);
}

/** Names for codes UN/LOCODE uses that are not ISO-3166 countries. */
const EXTRA_COUNTRY_NAMES: Record<string, string> = {
  XZ: 'International Waters', // UN/LOCODE: "Installations in International Waters"
};

/**
 * Get the English name for a country code, falling back to the code itself when unknown.
 * @param code - The ISO alpha-2 or alpha-3 country code.
 */
export function getCountryName(code: string): string {
  const upperCode = code.toUpperCase();
  return getCountry(upperCode)?.name ?? EXTRA_COUNTRY_NAMES[upperCode] ?? upperCode;
}

/** A country as a choice in a country control, or the "All countries" choice (`clear`), which stands for none. */
export type CountryOption = CountryInfo & { disabled?: boolean; clear?: boolean };

/** The "All countries" choice, which sets a country control's value back to `null`. */
export const ALL_COUNTRIES: CountryOption = {
  iso2: '',
  iso3: '',
  name: 'All countries',
  continent: '',
  phone: 0,
  clear: true,
};

/**
 * The choices for a country control: every country or only `countries`, with `disabledCountries` shown but not
 * allowed, and "All countries" (or `clearLabel`) first when `allowClear` is set. Codes may be alpha-2 or alpha-3.
 */
export function getCountryOptions({
  countries: only,
  disabledCountries,
  allowClear,
  clearLabel = ALL_COUNTRIES.name,
}: {
  countries?: string[];
  disabledCountries?: string[];
  allowClear?: boolean;
  clearLabel?: string;
}): CountryOption[] {
  const listed = only?.length ? countries.filter((c) => only.includes(c.iso3) || only.includes(c.iso2)) : countries;
  const options = listed.map((c) => ({
    ...c,
    disabled: disabledCountries?.includes(c.iso3) || disabledCountries?.includes(c.iso2),
  }));
  return allowClear ? [{ ...ALL_COUNTRIES, name: clearLabel }, ...options] : options;
}

/** The option a country control's value stands for, by alpha-2 or alpha-3 code, or `null`. */
export function findCountryOption(options: CountryOption[], value: string | null | undefined): CountryOption | null {
  return (value && options.find((c) => !c.clear && (c.iso3 === value || c.iso2 === value))) || null;
}
